import React, { useEffect, useState } from "react";
import RightArrow from "../../../../assets/Icons/Analytics/blackRightArrow.svg";
import DefaultEventBanner from "../../../../assets/Images/socha_banner.png";
import { ClipboardList, Eye, Info } from "lucide-react";
import RightSideDrawer from "../../../../components/rightDrawer";
import GuestAnswersView from "../../../../components/GuestAnswersView";
import { eventDetailService } from "../../../../services/eventDetailServices";
import { useSpinner } from "../../../../components/spinner/SpinnerContext";
import UserProfile from "../../../../screens/profile/userProfile";
import { useLocation, useNavigate } from "react-router-dom";
import {
  convertUtcToLocal,
  formatDateTime,
  formatUtcToLocal,
  getDayOfWeek,
  getMonthAndDay,
  getTime,
  isSameDay,
} from "../../../../utils/common";
import { toast } from "react-toastify";
import ConfirmationAlert from "./../../../../customComponents/ConfirmationAlert";
import { getLocalStorage } from "../../../../utils/localStorage";
import { LOCAL_STORAGE } from "../../../../utils/constants";

export interface PendingPaymentHold {
  holdId: number;
  userId: number;

  firstName: string;
  lastName: string;
  name: string;
  email: string;

  ticketName: string;
  totalTickets: number;
  amount: number;
  discountPercentage: number;
  promoCodeName?: string;
  paymentStatus: "Inprogress" | string;
  createdAt: string;
}

export default function PendingApprovalList({ eventName, bannerImage }: any) {
  const { setShowSpinner } = useSpinner();
  const navigate = useNavigate();
  const location = useLocation();
  //   const { eventData, eventStartDateTime, eventEndDateTime } = location.state;
  const queryParams = new URLSearchParams(location.search);
  const eventId: any = queryParams.get("eventid");
  const hostId: any = queryParams.get("hostid");
  const userId: any = getLocalStorage(LOCAL_STORAGE.USER_ID);
  const [eventDetails, setEventDetails] = useState<any>([]);
  const [guests, setGuests] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [eventStartDateTime, setEventStartDateTime] = useState("");
  const [eventEndDateTime, setEventEndDateTime] = useState("");

  const [isConnectionModalOpen, setIsConnectionModalOpen] = useState(false);

  const [actionType, setActionType] = useState<"approve" | "decline" | null>(
    null,
  );
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [guestPage, setGuestPage] = useState(1);

  const [paymentHolds, setPaymentHolds] = useState<PendingPaymentHold[]>([]);

  const [showGuestAnswersDrawer, setShowGuestAnswersDrawer] = useState(false);
  const [answersGuestId, setAnswersGuestId] = useState<number | null>(null);
  const [answersGuestName, setAnswersGuestName] = useState("");
  const [hasQuestions, setHasQuestions] = useState(false);

  const [paymentStatusFilter, setPaymentStatusFilter] = useState<
    "ALL" | "Inprogress" | "Approved" | "Declined"
  >("Inprogress");

  useEffect(() => {
    if (!eventId || !hostId) {
      toast.error(
        "Invalid event or host details. Please access this page from a valid event.",
      );
      return;
      //   navigate(-1); // or navigate("/events") if you prefer a safe route
    }
  }, []);

  useEffect(() => {
    if (eventId && hostId) {
      getHostEventDtls(eventId, hostId);
    }
  }, [eventId, hostId]);

  useEffect(() => {
    if (eventId && hostId) {
      fetchPendingPaymentHolds(1);
    }
  }, [eventId, hostId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchPendingPaymentHolds(1);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKey, paymentStatusFilter]);

  useEffect(() => {
    setEventStartDateTime(
      convertUtcToLocal(eventDetails?.startTime, eventDetails?.timezone),
    );
    setEventEndDateTime(
      convertUtcToLocal(eventDetails?.endTime, eventDetails?.timezone),
    );
  }, [eventDetails]);

  const getHostEventDtls = async (eventId?: number, hostId?: number) => {
    try {
      setShowSpinner(true);
      const res = await eventDetailService.getHostEventDetails({
        eventId,
        userId,
      });
      const eventData = res?.data?.events;
      if (res?.status) {
        setEventDetails(res?.data?.events);
        setEventStartDateTime(
          convertUtcToLocal(eventData?.startTime, eventData?.timezone),
        );
        setEventEndDateTime(
          convertUtcToLocal(eventData?.endTime, eventData?.timezone),
        );
        setHasQuestions(!!res?.data?.hasQuestions)
        setShowSpinner(false);
      }
    } catch (error: any) {
      console.error(
        "Error in getGuestEventDtls:",
        error?.response?.data?.error || error?.message,
      );
      setShowSpinner(false);
    } finally {
      setShowSpinner(false);
    }
  };

  const handleViewProfile = (user: any) => {
    setSelectedUser(user);
    setIsConnectionModalOpen(true);
  };

  const handleApprove = async (guestId?: any) => {
    try {
      setShowSpinner(true);
      const eventId = eventDetails?.eventId;
      const hostId = eventDetails?.hostUserId;
      const res = await eventDetailService.approvePaymentHoldTicket({
        eventId: Number(eventId),
        id: Number(guestId),
      });
      if (res.status) {
        toast.success("You have approved this guest's request");
        fetchPendingPaymentHolds(guestPage);
      }
      setShowSpinner(false);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.error || err.error || "Failed to approve",
      );
      setShowSpinner(false);
    }
  };

  const handleReject = async (guestId: number) => {
    try {
      setShowSpinner(true);
      const eventId = eventDetails?.eventId;
      const hostId = eventDetails?.hostUserId;
      const res = await eventDetailService.rejectPaymentHoldTicket({
        eventId: Number(eventId),
        id: Number(guestId),
      });
      if (res.status) {
        toast.success("You have rejected this guest's request");
        fetchPendingPaymentHolds(guestPage);
      }
      setShowSpinner(false);
    } catch (err) {
      toast.error("Failed to Decline");
      setShowSpinner(false);
    }
  };

  const fetchPendingPaymentHolds = async (page = 1) => {
    try {
      setShowSpinner(true);

      const res = await eventDetailService.getPendingPaymentHolds({
        eventId,
        hostId,
        pageNo: page,
        limit: 5,
        search: searchKey,
        status: paymentStatusFilter !== "ALL" ? paymentStatusFilter : undefined,
      });

      if (res?.status && res?.data?.holds) {
        const mappedHolds: PendingPaymentHold[] = res.data.holds.map(
          (item: any) => ({
            holdId: item.id,
            userId: item.userId,
            id: item.id,
            firstName: item.firstName,
            lastName: item.lastName,
            name: `${item.firstName} ${item.lastName}`,
            email: item.email,

            ticketName: item.ticketName,
            totalTickets: item.totalTickets,
            amount: item.amount,
            discountPercentage: item.discountPercentage,
            promoCodeName: item.promoCodeName,
            paymentStatus: item.paymentStatus,
            createdAt: item.createdAt,
          }),
        );

        setPaymentHolds(mappedHolds);
        setCurrentPage(res.data.currentPage);
        setTotalPages(res.data.totalPages);
        setTotalResults(res.data.total);
      }
    } catch {
      toast.error("Failed to load pending payments");
    } finally {
      setShowSpinner(false);
    }
  };

  const formatAmount = (value: number) => value.toFixed(2);

  const TOOLTIP_WIDTH = 256;
  const PADDING = 8;

  const InfoTooltip = () => {
    const [pos, setPos] = useState<{
      x: number;
      y: number;
    } | null>(null);

    return (
      <>
        <Info
          size={14}
          className="cursor-pointer text-red-700"
          onMouseEnter={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();

            // Clamp horizontally inside viewport
            const maxX = window.innerWidth - TOOLTIP_WIDTH - PADDING;
            const x = Math.min(Math.max(rect.left, PADDING), maxX);

            setPos({
              x,
              y: rect.top,
            });
          }}
          onMouseLeave={() => setPos(null)}
        />

        {pos && (
          <div
            className="fixed z-[9999] w-64 rounded-md border bg-white p-2 text-[11px] text-gray-600 shadow-lg"
            style={{
              left: pos.x,
              top: pos.y - 8,
              transform: "translateY(-100%)",
            }}
          >
            The bank's 7-day hold period has ended, and the amount has been
            refunded to the guest's account. Kindly ask your guest to
            re-purchase the ticket.
          </div>
        )}
      </>
    );
  };

  return (
    <div className="w-full flex flex-col px-4 md:px-8 py-4">
      {/* ================= HEADER ================= */}
      <div className="mb-6">
        <div
          className="flex items-center gap-2 cursor-pointer mb-2"
          onClick={() => navigate(-1)}
        >
          <img src={RightArrow} className="w-5 h-5 rotate-180" />
          <span className="text-base font-semibold text-gray-800">
            Back to Event
          </span>
        </div>

        <p className="text-sm md:text-lg text-gray-600">
          These requests are awaiting your approval before being processed.
        </p>
      </div>

      {/* ================= EVENT INFO + SEARCH ================= */}

      {eventDetails && eventId && hostId ? (
        <div className="w-full">
          <div className="flex flex-col md:flex-row md:items-center xl:justify-between gap-6 mb-6 items-center">
            {/* EVENT INFO */}
            {eventDetails?.eventName && (
              <>
                <div className="flex gap-4 bg-[#FAF2F6] p-4 rounded-2xl w-full xl:max-w-[50%]">
                  <img
                    src={eventDetails?.bannerImage || DefaultEventBanner}
                    className="w-20 h-20 rounded-xl object-cover shrink-0"
                  />

                  <div className="flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 truncate">
                      {eventDetails?.eventName}
                    </h2>

                    <p className="text-[10px] md:text-xs text-gray-600">
                      <div className="flex md:gap-1 items-center">
                        <span>
                          {getDayOfWeek(eventStartDateTime) +
                            " " +
                            getMonthAndDay(eventStartDateTime)}
                        </span>
                        <span className="mx-1">-</span>
                        <span className="text-[#0F090B]">
                          {getTime(eventStartDateTime)}
                        </span>
                        <span className="mx-1 text-[#786469]">to</span>
                        {isSameDay(eventStartDateTime, eventEndDateTime) ? (
                          <span className="text-[#0F090B]">
                            {getTime(eventEndDateTime)}
                          </span>
                        ) : (
                          <div className="flex md:gap-1">
                            <span>
                              {getDayOfWeek(eventEndDateTime) +
                                " " +
                                getMonthAndDay(eventEndDateTime)}
                            </span>
                            <span className="mx-1">-</span>
                            <span className="text-[#0F090B]">
                              {getTime(eventEndDateTime)}
                            </span>
                          </div>
                        )}
                      </div>
                    </p>

                    {eventDetails?.location && (
                      <p className="text-[10px] md:text-xs text-gray-700 mt-1 flex items-start gap-1 truncate min-w-0">
                        <span>📍</span>
                        <span className="line-clamp-2">
                          {eventDetails?.location}
                        </span>
                      </p>
                    )}
                  </div>
                </div>

                {/* SEARCH */}
                {guests.length > 0 && (
                  <div className="flex gap-2 w-full xl:max-w-[320px]">
                    <input
                      value={searchKey}
                      onChange={(e) => setSearchKey(e.target.value)}
                      placeholder="Search guest"
                      className="flex-1 border rounded-lg px-3 py-2 text-sm"
                    />
                    <button
                      onClick={() => fetchPendingPaymentHolds(1)}
                      className="bg-black text-white px-4 py-2 rounded-lg text-sm"
                    >
                      Search
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex w-full justify-between items-center mb-4">
            <div className="text-lg font-semibold text-gray-800 hidden md:block">
              <h2 className="text-lg font-semibold text-gray-800">
                {/* Payment Hold Approvals */}
              </h2>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto md:justify-end">
              {/* SEARCH */}
              <div className="relative w-full sm:w-64">
                <input
                  value={searchKey}
                  onChange={(e) => setSearchKey(e.target.value)}
                  placeholder="Search name or email"
                  className="w-full border rounded-lg pl-9 pr-9 py-2 text-sm"
                />

                {/* Search Icon */}
                <span
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
                  onClick={() => fetchPendingPaymentHolds(1)}
                >
                  🔍
                </span>

                {/* Clear Icon */}
                {searchKey && (
                  <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
                    onClick={() => {
                      setSearchKey("");
                      fetchPendingPaymentHolds(1);
                    }}
                  >
                    ✕
                  </span>
                )}
              </div>

              {/* STATUS FILTER */}
              <select
                value={paymentStatusFilter}
                onChange={(e) => {
                  setPaymentStatusFilter(e.target.value as any);
                  fetchPendingPaymentHolds(1);
                }}
                className="border rounded-lg px-3 py-2 text-sm w-full sm:w-44"
              >
                <option value="ALL">All Status</option>
                <option value="Inprogress">Approval Required</option>
                <option value="Approved">Approved</option>
                <option value="Declined">Declined</option>
              </select>
            </div>
          </div>

          {/* ================= CONTENT ================= */}
          <div className="flex-1 overflow-y-auto">
            {/* DESKTOP TABLE */}
            {/* ================= DESKTOP TABLE ================= */}
            <div className="hidden md:block">
              {paymentHolds.length > 0 ? (
                <table className="w-full border-separate border-spacing-y-2">
                  <thead>
                    <tr className="text-sm text-gray-500 text-left">
                      <th className="px-4">User</th>
                      <th className="px-4">Ticket</th>
                      <th className="px-4">Amount</th>
                      <th className="px-4 text-center">Status</th>
                      <th className="px-4">Requested On</th>
                      <th className="px-4 text-center">View</th>
                     {hasQuestions && <th className="px-4 text-center">Answers</th>}
                      <th className="px-4 text-center">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paymentHolds.map((row) => (
                      <tr
                        key={row.holdId}
                        className="bg-[#FAF2F6] hover:bg-[#F2DDE2]"
                      >
                        {/* USER */}
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800">
                            {row.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {row.email}
                          </div>
                        </td>

                        {/* TICKET */}
                        <td className="px-4 text-sm">
                          <div className="font-medium">{row.ticketName}</div>
                          <div className="text-xs text-gray-500">
                            {row.totalTickets} ticket
                            {row.totalTickets > 1 ? "s" : ""}
                          </div>
                        </td>

                        {/* AMOUNT */}
                        <td className="px-4 py-3">
                          <div className="font-semibold">
                            ${formatAmount(row.amount)}
                          </div>

                          <div className="flex flex-col">
                            {/* Promo Code */}
                            {row.promoCodeName && (
                              <div className="text-xs text-indigo-600">
                                Promo: {row.promoCodeName || "N/A"}
                              </div>
                            )}
                            {/* Discount */}
                            {row.discountPercentage > 0 && (
                              <div className="text-xs text-gray-500">
                                Discount: {row.discountPercentage || 0.0}%
                              </div>
                            )}
                          </div>
                        </td>

                        {/* STATUS */}
                        <td className="px-4 py-3 ">
                          <div className="flex justify-start md:justify-center">
                            <div className="flex items-center gap-1">
                              {/* Status badge */}
                              <span
                                className={`px-2 py-1 text-xs rounded-full ${
                                  row.paymentStatus === "Inprogress"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : row.paymentStatus === "Approved"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-red-100 text-red-800"
                                }`}
                              >
                                {row.paymentStatus === "Inprogress"
                                  ? "Approval Required"
                                  : row.paymentStatus}
                              </span>

                              {/* Info tooltip (Expired only) */}
                              {row.paymentStatus?.trim().toLowerCase() ===
                                "expired" && <InfoTooltip />}
                            </div>
                          </div>
                        </td>

                        {/* Requested On */}
                        <td className="px-4">
                          {/* Assuming you have a createdAt field in row */}
                          <div className="text-sm text-gray-800">
                            {row?.createdAt
                              ? formatUtcToLocal(row.createdAt)
                              : "N/A"}
                          </div>
                        </td>

                        {/* VIEW */}
                        <td className="px-4 text-center">
                          <button
                            className="px-3 py-1 bg-purple-600 text-white rounded-lg text-xs"
                            onClick={() => handleViewProfile(row)}
                          >
                            View
                          </button>
                        </td>

                        {/* ANSWERS */}
                        {hasQuestions && <td className="px-4 text-center">
                          <button
                            className="flex items-center justify-center mx-auto bg-[#FAF2F6] hover:bg-[#F1E7EC] rounded-lg p-1 transition-colors"
                            title="View Submitted Answers"
                            onClick={() => {
                              setAnswersGuestId(row.userId);
                              setAnswersGuestName(row.name);
                              setShowGuestAnswersDrawer(true);
                            }}
                          >
                            <ClipboardList size={18} className="text-[#0F090B]" />
                          </button>
                        </td>}

                        {/* ACTIONS */}
                        <td className="px-4 text-center">
                          {row.paymentStatus === "Inprogress" && (
                            <div className="flex gap-2 justify-center">
                              <button
                                className="px-3 py-1 bg-green-600 text-white rounded-lg text-xs"
                                //   onClick={() => openApproveConfirm(row)}
                                onClick={() => {
                                  setSelectedUser(row);
                                  setActionType("approve");
                                }}
                              >
                                Approve
                              </button>

                              <button
                                className="px-3 py-1 bg-red-600 text-white rounded-lg text-xs"
                                //   onClick={() => openRejectConfirm(row)}
                                onClick={() => {
                                  setSelectedUser(row);
                                  setActionType("decline");
                                }}
                              >
                                Decline
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-center py-6 text-gray-600">
                  You don’t have any pending RSVP requests at the moment.
                </p>
              )}
            </div>

            {/* ================= MOBILE CARDS ================= */}
            <div className="md:hidden flex flex-col gap-4 mb-3">
              {paymentHolds.length > 0 ? (
                paymentHolds.map((row) => (
                  <div
                    key={row.holdId}
                    className="bg-white rounded-2xl p-4 shadow-sm"
                  >
                    {/* USER */}
                    <div className="mb-2">
                      <div className="w-full flex justify-between">
                        <div className="flex items-center w-full gap-2">
                          {/* Left block: Name */}
                          <div className="w-3/5 min-w-0 flex flex-col">
                            <span className="font-semibold text-gray-800 break-words line-clamp-2">
                              {row.name}
                            </span>
                          </div>

                          {/* Right block: Eye icon + Answers icon + Status */}
                          <div className="w-2/5 flex items-center justify-end gap-2">
                            {/* Eye icon */}
                            <div className="flex justify-center items-center flex-shrink-0">
                              <Eye
                                className="w-6 h-6 cursor-pointer text-indigo-600 hover:text-indigo-800"
                                onClick={() => handleViewProfile(row)}
                              />
                            </div>

                            {/* Answers icon */}
                            {hasQuestions && (
                              <div className="flex justify-center items-center flex-shrink-0">
                                <ClipboardList
                                  className="w-5 h-5 cursor-pointer text-[#0F090B] bg-[#FAF2F6] hover:bg-[#F1E7EC] rounded-md p-0.5 transition-colors"
                                  onClick={() => {
                                    setAnswersGuestId(row.userId);
                                    setAnswersGuestName(row.name);
                                    setShowGuestAnswersDrawer(true);
                                  }}
                                />
                              </div>
                            )}

                            {/* Payment status */}
                            <span
                              className={`py-1.5 px-2 text-[9px] rounded-full whitespace-nowrap ${
                                row.paymentStatus === "Inprogress"
                                  ? "bg-yellow-100 text-yellow-800"
                                  : row.paymentStatus === "Approved"
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                              }`}
                            >
                              {row.paymentStatus === "Inprogress"
                                ? "Approval Required"
                                : row.paymentStatus}
                            </span>
                            {row.paymentStatus?.trim().toLowerCase() ===
                              "expired" && <InfoTooltip />}
                          </div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">{row.email}</p>
                    </div>

                    {/* TICKET */}
                    <div className="w-full flex justify-between items-center mb-2">
                      <div className="text-sm mb-2">
                        <p className="font-medium">{row.ticketName}</p>
                        <p className="text-xs text-gray-500">
                          {row.totalTickets} ticket
                          {row.totalTickets > 1 ? "s" : ""}
                        </p>
                      </div>

                      <div className="text-sm mb-2" key={row.holdId}>
                        <p className="font-medium">Requested On</p>
                        <p className="text-xs text-gray-500">
                          {row?.createdAt
                            ? formatUtcToLocal(row.createdAt)
                            : "N/A"}
                        </p>
                      </div>
                    </div>

                    {/* AMOUNT + STATUS */}
                    <div className="flex justify-between items-center mb-3">
                      <p className="font-semibold">${row.amount}</p>

                      <div className="flex flex-col">
                        {/* Promo Code */}
                        {row.promoCodeName && (
                          <div className="text-xs text-indigo-600">
                            Promo: {row.promoCodeName || "N/A"}
                          </div>
                        )}
                        {/* Discount */}
                        {row.discountPercentage > 0 && (
                          <div className="text-xs text-gray-500">
                            Discount: {row.discountPercentage || 0.0}%
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ACTIONS */}
                    {row.paymentStatus === "Inprogress" && (
                      <div className="flex gap-3">
                        <button
                          className="flex-1 py-2 bg-green-600 text-white rounded-xl text-sm"
                          // onClick={() => openApproveConfirm(row)}
                          onClick={() => {
                            setSelectedUser(row);
                            setActionType("approve");
                          }}
                        >
                          Approve
                        </button>

                        <button
                          className="flex-1 py-2 bg-red-600 text-white rounded-xl text-sm"
                          // onClick={() => openRejectConfirm(row)}
                          onClick={() => {
                            setSelectedUser(row);
                            setActionType("decline");
                          }}
                        >
                          Decline
                        </button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <p className="text-center py-6 text-gray-600">
                  You don’t have any pending RSVP requests at the moment.
                </p>
              )}
            </div>
          </div>

          {/* ================= PAGINATION ================= */}
          {paymentHolds.length > 0 && totalResults > 5 && totalPages > 0 && (
            <div className="border-t bg-white py-3 px-4 flex justify-between items-center">
              <button
                disabled={currentPage === 1}
                onClick={() => fetchPendingPaymentHolds(currentPage - 1)}
                className="px-3 py-1 bg-black text-white rounded disabled:bg-gray-300"
              >
                Prev
              </button>

              <span className="text-sm">
                Page {currentPage} of {totalPages}
              </span>

              <button
                disabled={currentPage === totalPages}
                onClick={() => fetchPendingPaymentHolds(currentPage + 1)}
                className="px-3 py-1 bg-black text-white rounded disabled:bg-gray-300"
              >
                Next
              </button>
            </div>
          )}

          {/* ================= USER PROFILE DRAWER ================= */}
          {isConnectionModalOpen && selectedUser && (
            <RightSideDrawer
              open
              onClose={() => setIsConnectionModalOpen(false)}
              title="Guest Profile"
              paymentDrawer
              className="md:w-[65vw] lg:w-[55vw] side-drawer-connection-bg"
            >
              <UserProfile
                eventId={eventDetails?.eventId}
                hostId={eventDetails?.hostUserId}
                selectedUserId={selectedUser.userId}
                userObj={selectedUser}
                isHost={true}
              />
            </RightSideDrawer>
          )}

          {selectedUser && !!actionType && (
            <ConfirmationAlert
              open={!!actionType}
              onClose={() => {
                setActionType(null);
                setSelectedUser(null);
              }}
              title={`${
                actionType === "approve" ? "Approve" : "Reject"
              } Request`}
              message={
                <>
                  <p className="text-gray-700">
                    Are you sure you want to <strong>{actionType}</strong> the
                    request for{" "}
                    <strong>
                      {selectedUser?.firstName} {selectedUser?.lastName}
                    </strong>
                    ?
                  </p>

                  <div className="mt-3 text-sm text-gray-700">
                    <p>
                      <strong>Ticket:</strong> {selectedUser?.ticketName}
                    </p>
                    <p>
                      <strong>Quantity:</strong> {selectedUser?.totalTickets}
                    </p>
                    <p>
                      <strong>Total Amount:</strong> $
                      {formatAmount(selectedUser?.amount)}
                    </p>

                    {selectedUser?.discountPercentage > 0 && (
                      <p>
                        <strong>Discount:</strong>{" "}
                        {selectedUser.discountPercentage}%
                      </p>
                    )}

                    {selectedUser?.promoCodeName && (
                      <p>
                        <strong>Promo Code:</strong>{" "}
                        {selectedUser.promoCodeName}
                      </p>
                    )}
                  </div>

                  {actionType === "approve" && (
                    <p className="mt-3 text-xs text-green-500">
                      Once approved, the payment will be processed and the
                      ticket will be confirmed.
                    </p>
                  )}
                </>
              }
              onConfirm={() => {
                if (actionType === "approve") {
                  handleApprove(selectedUser?.id);
                } else {
                  handleReject(Number(selectedUser?.id));
                }
                setActionType(null);
                setSelectedUser(null);
              }}
              confirmText={actionType === "approve" ? "Approve" : "Decline"}
            />
          )}
          {/* ================= GUEST ANSWERS DRAWER ================= */}
          {showGuestAnswersDrawer && answersGuestId && (
            <RightSideDrawer
              open={showGuestAnswersDrawer}
              onClose={() => setShowGuestAnswersDrawer(false)}
              title="Guest Responses"
              paymentDrawer={true}
              flexContent={true}
              className="md:w-[50vw] lg:w-[35vw] side-drawer-connection-bg"
            >
              <div className="flex flex-col flex-1 min-h-0">
                {answersGuestName && (
                  <p className="text-base font-semibold text-[#0F090B] mb-3 shrink-0">
                    {answersGuestName}'s Responses
                  </p>
                )}
                <GuestAnswersView
                  eventId={Number(eventId)}
                  guestId={Number(answersGuestId)}
                  isHost={true}
                />
              </div>
            </RightSideDrawer>
          )}
        </div>
      ) : (
        <p className="text-center py-6 text-gray-600 w-full">
          No guests found!
        </p>
      )}
    </div>
  );
}
